import React from 'react';
import { cva } from 'class-variance-authority';
import clsx from 'clsx';
import { motion, useReducedMotion } from 'motion/react';
import { Popover } from './Popover';
import styles from './PageCard.module.css';

const pageCard = cva(styles.base, {
  variants: {
    variant: { default: '', pastel: styles.pastel },
  },
  defaultVariants: { variant: 'default' },
});

type DivMotionSafeProps = Omit<
  React.HTMLAttributes<HTMLDivElement>,
  'onDrag' | 'onDragEnd' | 'onDragStart' | 'onAnimationStart'
>;

export interface PageCardProps extends DivMotionSafeProps {
  /** Scaled-down preview content (e.g. MarkupPreview or a collection mini-stack) */
  preview: React.ReactNode;
  /** Document or collection title */
  title: string;
  /** Secondary line on the right of the footer (e.g. "Apr 19" or "12 docs"). */
  meta: string;
  /** Optional type icon shown before the title in the footer. */
  icon?: React.ReactNode;
  /** Hover-revealed overlay content at top-right (e.g. a dropdown trigger) */
  actions?: React.ReactNode;
  /** Overlay content at top-left (e.g. a selection checkbox) */
  actionsLeft?: React.ReactNode;
  /** Visual variant — 'pastel' applies --page-card-bg */
  variant?: 'default' | 'pastel';
  /** Number of documents in the stack. When set, renders fan shadows + count badge. */
  stack?: number;
  /** Content rendered inside the stack popover when the card is clicked. Requires stack to be set. */
  stackPopoverContent?: React.ReactNode;
  /** Called when the card is clicked and stack is set. Overrides onClick. */
  onStackClick?: () => void;
}

/**
 * PageCard — tall portrait card primitive for document / collection grids.
 *
 * Aspect ratio 3:4 — a sheet of paper. The preview fills the whole card. On
 * hover the card lifts, the border turns primary, the `actions` slot reveals at
 * the top-right, and a meta overlay (title + `meta`) fades in at the bottom.
 *
 * Use with `PageCardGrid` for responsive layout and `MarkupPreview` for
 * scaled-down HTML/SVG previews.
 *
 * @example Basic usage
 * ```tsx
 * <PageCard preview={<MarkupPreview markup={html} />} title="My Doc" meta="Document · Apr 19" />
 * ```
 *
 * @example Pastel variant
 * The `pastel` variant reads its background (card fill and fade) from the
 * `--page-card-bg` CSS custom property. Set it on the card itself via `style`:
 * ```tsx
 * <PageCard
 *   variant="pastel"
 *   style={{ '--page-card-bg': 'linear-gradient(150deg, #ffd6e0, #ffb3c6)' } as React.CSSProperties}
 *   preview={preview}
 *   title="My Doc"
 *   meta="Document · Apr 19"
 * />
 * ```
 * If `--page-card-bg` is not set, the pastel variant falls back to the default
 * surface color.
 */
export function PageCard({
  preview,
  title,
  meta,
  icon,
  actions,
  actionsLeft,
  variant = 'default',
  stack,
  stackPopoverContent,
  onStackClick,
  className,
  onClick,
  ...props
}: PageCardProps) {
  const shouldReduce = useReducedMotion();

  const handleClick = onStackClick && stack != null
    ? (e: React.MouseEvent<HTMLDivElement>) => { e.stopPropagation(); onStackClick(); }
    : onClick;

  // The hover-lift lives on this OUTER motion wrapper, never on the bordered
  // `.base` element. Transforming the bordered element composites its rounded
  // ::after border onto its own layer, which frays the indigo hover border
  // (ragged / uneven thickness). Lifting a non-bordered wrapper keeps the card's
  // rounded border on the base layer, so it stays crisp.
  const card = (
    <motion.div
      whileHover={shouldReduce ? {} : { y: -8 }}
      className={clsx(styles.lift, className)}
      onClick={handleClick}
      {...props}
    >
      <div className={pageCard({ variant })}>
        <div className={styles.preview}>
          {preview}
          <div className={styles.fade} aria-hidden="true" />
        </div>
        {actionsLeft && (
          <div className={styles.actionsLeft} onClick={(e) => e.stopPropagation()}>
            {actionsLeft}
          </div>
        )}
        {actions && (
          <div className={styles.actions} onClick={(e) => e.stopPropagation()}>
            {actions}
          </div>
        )}
        <div className={styles.footer} data-testid="paper-meta">
          <div className={styles.footerLeft}>
            {icon && <span className={styles.icon}>{icon}</span>}
            <span className={styles.title}>{title}</span>
          </div>
          {stack != null
            ? <span className={styles.stackBadge} data-testid="stack-badge">{stack}</span>
            : <span className={styles.date}>{meta}</span>
          }
        </div>
      </div>
    </motion.div>
  );

  if (stack != null) {
    const wrap = (
      <div className={styles.stackWrap}>
        <div className={styles.stackShadowB} data-testid="stack-shadow-2" aria-hidden="true" />
        <div className={styles.stackShadowA} data-testid="stack-shadow-1" aria-hidden="true" />
        {card}
      </div>
    );

    if (stackPopoverContent) {
      return (
        <Popover
          content={stackPopoverContent}
          trigger="click"
          placement="right-start"
          arrow={false}
        >
          {wrap}
        </Popover>
      );
    }

    return wrap;
  }

  return card;
}
